/**
 * Centralized portal access helpers — prefer auth/me fields over localStorage role alone.
 * Active account / portal / effective_role from canonical account-context beat bare users.role.
 */

import {
  AGENCY_ROLES,
  HQ_STAFF_OPERATIONS_ROLES,
  SELF_REPAIR_ROLES,
  isPlatformConsoleRole,
} from './hostnamePortal';

const AGENCY_WORKSPACE_ROLES = new Set([
  'agency_owner',
  'agency_admin',
  'agency_staff',
  'agency_user',
  'owner',
  'admin',
  'staff',
  'team_member',
  'employee',
  'specialist',
]);

const MANAGED_CLIENT_ROLES = new Set(['managed_client']);

const AGENCY_PORTALS = new Set(['agency', 'agency_staff', 'funding_hq']);
const AGENCY_ACCOUNT_TYPES = new Set(['agency', 'subaccount', 'funding_hq']);

function normalizeRole(user) {
  return String(
    user?.effective_role
    || user?.active_account?.effective_role
    || user?.active_account?.role
    || user?.role
    || ''
  ).toLowerCase();
}

function resolvePortal(user) {
  return String(
    user?.portal
    || user?.portal_type
    || user?.active_account?.portal
    || user?.selected_account?.portal
    || ''
  ).toLowerCase();
}

function resolveAccountType(user) {
  return String(
    user?.account_type
    || user?.active_account?.type
    || user?.selected_account?.account_type
    || ''
  ).toLowerCase();
}

export function hasValidAgencyId(user) {
  const portal = resolvePortal(user);
  const accountType = resolveAccountType(user);
  const role = normalizeRole(user);
  // Self-repair / platform sessions must never treat ids as agency routing keys.
  if (
    portal === 'self_repair'
    || accountType === 'self_repair'
    || portal === 'platform'
    || accountType === 'platform'
    || String(user?.portal_type || '').toLowerCase() === 'self_serve_portal'
    || (
      SELF_REPAIR_ROLES.includes(role)
      && role !== 'managed_client'
      && !AGENCY_WORKSPACE_ROLES.has(role)
    )
  ) {
    return false;
  }

  const agencyId = user?.agency_id || user?.selected_account?.agency_id || null;
  // Only treat active_account.account_id as agency when the account itself is agency-scoped.
  const activeType = String(
    user?.active_account?.type
    || user?.active_account?.account_type
    || ''
  ).toLowerCase();
  const activePortal = String(user?.active_account?.portal || '').toLowerCase();
  const activeLooksAgency =
    AGENCY_ACCOUNT_TYPES.has(activeType)
    || AGENCY_PORTALS.has(activePortal);
  const fromActive = activeLooksAgency
    ? (user?.active_account?.agency_id || user?.active_account?.account_id)
    : null;

  const resolved = agencyId || fromActive;
  return Boolean(resolved && resolved !== 'default_agency' && resolved !== 'acct_platform');
}

export function isSelfRepairUser(user) {
  if (!user) return false;

  const portal = resolvePortal(user);
  const accountType = resolveAccountType(user);
  const role = normalizeRole(user);
  const portalType = String(user.portal_type || '').toLowerCase();

  // Canonical portal/account_type wins — never let a stale agency_id flip this.
  if (portal === 'self_repair' || accountType === 'self_repair') {
    return true;
  }
  // Self Repair roles win over stale membership type=agency / portal=agency
  // (legacy account_id may still equal an agency id).
  if (
    SELF_REPAIR_ROLES.includes(role)
    && role !== 'managed_client'
    && !AGENCY_WORKSPACE_ROLES.has(role)
  ) {
    return true;
  }
  if (portalType === 'self_serve_portal') {
    return true;
  }
  if (AGENCY_PORTALS.has(portal) || AGENCY_ACCOUNT_TYPES.has(accountType) || portal === 'platform') {
    return false;
  }

  if (user.is_client === true && user.is_staff !== true) {
    if (portalType === 'managed_client_portal') return false;
    if (portalType === 'staff_portal') return false;
    return true;
  }

  if (AGENCY_WORKSPACE_ROLES.has(role)) return false;

  return false;
}

export function isManagedClientUser(user) {
  if (!user) return false;
  const portal = resolvePortal(user);
  if (AGENCY_PORTALS.has(portal) || portal === 'platform' || portal === 'self_repair') {
    return false;
  }
  if (portal === 'client') return true;
  if (user.is_client === true && String(user.portal_type || '').toLowerCase() === 'managed_client_portal') {
    return true;
  }
  return MANAGED_CLIENT_ROLES.has(normalizeRole(user));
}

export function isClientPortalUser(user) {
  const role = normalizeRole(user);
  // Self Repair / client roles win over stale membership type=agency.
  if (isSelfRepairUser(user) || isManagedClientUser(user)) {
    return true;
  }
  // Agency / platform workspace always wins over a stale users.role=client.
  const portal = resolvePortal(user);
  const accountType = resolveAccountType(user);
  if (AGENCY_PORTALS.has(portal) || AGENCY_ACCOUNT_TYPES.has(accountType) || portal === 'platform') {
    return false;
  }
  if (user?.is_staff === true && user?.is_client !== true) {
    return false;
  }
  if (AGENCY_WORKSPACE_ROLES.has(role) && hasValidAgencyId(user)) {
    return false;
  }
  return false;
}

export function isPlatformAdmin(user) {
  if (!user) return false;
  if (resolvePortal(user) === 'platform' || resolveAccountType(user) === 'platform') {
    return true;
  }
  if (user.platform_access === true) return true;
  if (user.is_platform_admin === true) return true;
  return isPlatformConsoleRole(normalizeRole(user));
}

export function isAgencyUser(user) {
  if (!user) return false;

  const portal = resolvePortal(user);
  const accountType = resolveAccountType(user);
  const role = normalizeRole(user);
  // Canonical self-repair session can never be classified as Agency — even if
  // legacy users.agency_id or membership metadata is still present.
  if (portal === 'self_repair' || accountType === 'self_repair') {
    return false;
  }
  if (String(user?.portal_type || '').toLowerCase() === 'self_serve_portal') {
    return false;
  }
  if (
    SELF_REPAIR_ROLES.includes(role)
    && role !== 'managed_client'
    && !AGENCY_WORKSPACE_ROLES.has(role)
  ) {
    return false;
  }
  if (isClientPortalUser(user)) return false;

  if (AGENCY_PORTALS.has(portal) || AGENCY_ACCOUNT_TYPES.has(accountType)) {
    return hasValidAgencyId(user);
  }

  if (isPlatformAdmin(user)) return false;

  if (HQ_STAFF_OPERATIONS_ROLES.includes(role) && !hasValidAgencyId(user)) {
    return false;
  }

  if (AGENCY_WORKSPACE_ROLES.has(role) || AGENCY_ROLES.includes(role)) {
    return hasValidAgencyId(user);
  }

  if ((role === 'owner' || role === 'admin') && hasValidAgencyId(user)) {
    if (user.platform_access === true) return false;
    return true;
  }

  if (
    hasValidAgencyId(user) &&
    (String(user.portal || '').toLowerCase() === 'agency' ||
      String(user.account_type || '').toLowerCase() === 'agency')
  ) {
    return true;
  }

  return false;
}

export function hasAgencyAccess(user, agencyId) {
  if (!user || !agencyId) return false;
  if (isPlatformAdmin(user)) return true;
  if (!isAgencyUser(user)) return false;
  if (isSelfRepairUser(user)) return false;
  const userAgency =
    user.agency_id
    || (
      // Never treat self_repair membership account_id as agency access.
      ['agency', 'subaccount', 'funding_hq'].includes(
        String(user.active_account?.type || user.active_account?.account_type || '').toLowerCase()
      )
        ? (user.active_account?.agency_id || user.active_account?.account_id)
        : user.active_account?.agency_id
    )
    || user.selected_account?.agency_id;
  return String(userAgency) === String(agencyId);
}

export function getClientPortalDashboardPath(user) {
  if (isManagedClientUser(user)) return '/client/dashboard';
  return '/dashboard';
}
